import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Heart, X, Flag } from "lucide-react";

export default function UserProfile() {
  const { id } = useParams();
  const [u, setU] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/profile/${id}`); setU(data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const act = async (interested) => {
    setBusy(true);
    try {
      const { data } = await api.post("/discover/interest", { other_user_id: id, interested });
      if (data.mutual) toast.success("It's mutual! A conversation is now open.");
      else toast(interested ? "Interest sent." : "Passed with kindness.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const report = async () => {
    const reason = prompt("What's the reason? (harassment, fake, safety)");
    if (!reason) return;
    try { await api.post("/reports", { target_user_id: id, reason }); toast.success("Thanks — our team will review this."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!u) return <div className="text-navy/50">Loading…</div>;
  const p = u.profile || {};
  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <Link to="/app/discover" className="text-sm text-navy/60 hover:text-navy" data-testid="user-back">← Back</Link>
      <div className="glass rounded-3xl p-8">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-peach to-rose flex items-center justify-center text-white font-display text-3xl">
              {(u.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
            <div>
              <div className="font-display text-4xl text-navy">{u.name}, {u.age}</div>
              <div className="text-navy/60">{u.city || "Somewhere lovely"}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-display text-sage">{u.compatibility?.score}%</div>
            <div className="text-[10px] uppercase tracking-widest text-navy/60">compatibility</div>
          </div>
        </div>
        {u.photos_hidden && (
          <div className="mt-6 rounded-2xl border border-rose/20 bg-rose/5 p-4 text-sm text-rose">
            Photos will be revealed once you both express interest. Read the mind first.
          </div>
        )}
        {u.compatibility?.shared_values?.length > 0 && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-widest text-navy/60 mb-2">Shared values</div>
            <div className="flex flex-wrap gap-2">
              {u.compatibility.shared_values.map((v) => (
                <span key={v} className="text-xs px-3 py-1.5 rounded-full bg-rose/10 text-rose">{v}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <Section title="My story" text={p.story} testid="sec-story" />
      <Section title="My dreams" text={p.dreams} testid="sec-dreams" />
      <Section title="Happy place" text={p.happy_place} testid="sec-happy" />
      <Section title="Personal motto" text={p.personal_motto} italic testid="sec-motto" />
      <Section title="Favourite quote" text={p.favourite_quote} italic testid="sec-quote" />

      <div className="grid md:grid-cols-2 gap-4">
        <MetaCard label="Relationship goals" v={p.relationship_goals} />
        <MetaCard label="Children" v={p.children} />
        <MetaCard label="Love language" v={p.love_language} />
        <MetaCard label="Communication" v={p.communication} />
        <MetaCard label="Faith" v={p.faith} />
        <MetaCard label="Adventure level" v={p.adventure_level ? `${p.adventure_level}/5` : null} />
      </div>

      <div className="glass rounded-3xl p-6 flex gap-3">
        <button data-testid="user-pass" disabled={busy} onClick={() => act(false)} className="flex-1 btn-pill bg-white/70 border border-navy/10 text-navy hover:bg-white flex items-center justify-center gap-2">
          <X size={16} /> Pass kindly
        </button>
        <button data-testid="user-interest" disabled={busy} onClick={() => act(true)} className="flex-1 btn-pill bg-rose text-white hover:bg-rose-600 flex items-center justify-center gap-2 hover:scale-[1.02]">
          <Heart size={16} /> Express interest
        </button>
        <button data-testid="user-report" onClick={report} className="btn-pill bg-white/70 border border-navy/10 text-navy hover:bg-white flex items-center gap-2">
          <Flag size={16} /> Report
        </button>
      </div>

      {err && <div className="text-sm text-rose-600" data-testid="user-error">{err}</div>}
    </div>
  );
}

const Section = ({ title, text, italic, testid }) => text ? (
  <div className="glass rounded-3xl p-6" data-testid={testid}>
    <div className="text-xs uppercase tracking-widest text-rose">{title}</div>
    <div className={`mt-2 text-navy/85 leading-relaxed ${italic ? "italic" : ""}`}>{italic ? `"${text}"` : text}</div>
  </div>
) : null;

const MetaCard = ({ label, v }) => (
  <div className="glass rounded-2xl p-5">
    <div className="text-xs uppercase tracking-widest text-navy/60">{label}</div>
    <div className="mt-1 text-navy">{v || <span className="text-navy/40">—</span>}</div>
  </div>
);
